import type { MembershipRole } from '@vtak/shared';

/**
 * The org-identity claims carried in the Vertex token. Keycloak nests dotted
 * claim names, so the mappers emit a single nested `vertex` object rather than
 * flat `vertex.*` keys. Mirrors the published `@vertex/identity-client` parser;
 * kept in-repo to avoid a cross-repo package dependency.
 */
export interface VertexClaims {
  branch: string | null;
  orgSlug: string | null;
  primaryUnitId: string | null;
  unitShort: string | null;
  unitName: string | null;
  orgPath: string[];
  unitRole: MembershipRole | null;
}

/** Returns a non-empty string field, or null. */
function str(v: Record<string, unknown>, key: string): string | null {
  const value = v[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/** Narrows a value to a membership role, or null. */
function toRole(value: string | null): MembershipRole | null {
  return value === 'member' || value === 'admin' ? value : null;
}

/** Reads the org-path field (array or comma-delimited string). */
function toOrgPath(v: Record<string, unknown>): string[] {
  const value = v.org_path;
  if (Array.isArray(value)) return value.filter((x): x is string => typeof x === 'string');
  if (typeof value !== 'string' || value.length === 0) return [];
  return value.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
}

/** Parse the nested `vertex` claim out of a decoded token payload. */
export function parseVertexClaims(tokenParsed: Record<string, unknown> | undefined): VertexClaims {
  const raw = tokenParsed?.['vertex'];
  const v: Record<string, unknown> = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
  return {
    branch: str(v, 'branch'),
    orgSlug: str(v, 'org_slug'),
    primaryUnitId: str(v, 'primary_unit_id'),
    unitShort: str(v, 'unit_short'),
    unitName: str(v, 'unit_name'),
    orgPath: toOrgPath(v),
    unitRole: toRole(str(v, 'unit_role')),
  };
}
